import React, { createRef, forwardRef } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import Accordion, { type AccordionProps } from "@mui/material/Accordion";

// Every other prop is spread onto MUI's `Accordion`; `border`/`borderRadius` build the `sx` below and are held
// back from the spread (MUI hands an unknown prop to its DOM element, where React warns about `borderRadius`). `sx`
// also admits `false` because a caller writes `sx={cond && {...}}`.
type AccordianProps = Omit<AccordionProps, "children" | "sx"> & {
  children?: React.ReactNode;
  border?: string;
  borderRadius?: string;
  sx?: AccordionProps["sx"] | false;
};

// MUI's `Accordion` renders a `Paper` div, so the ref it forwards is an `HTMLDivElement`; the
// wider `HTMLElement` this used to declare had to be cast back down at the `ref` prop.
const Accordian = forwardRef((props: AccordianProps, ref: React.ForwardedRef<HTMLDivElement>) => {
  const { border, borderRadius, sx, ...accordionProps } = props;
  const accordianRef = ref ?? createRef<HTMLDivElement>();

  // VENDORED DECLARATION, narrower than what MUI ships. `AccordionProps["children"]` is declared
  // `NonNullable<React.ReactNode>` (node_modules/@mui/material/Accordion/Accordion.d.ts:62), but the
  // implementation only does `const [summary, ...children] = React.Children.toArray(childrenProp)`
  // (node_modules/@mui/material/Accordion/Accordion.js:170), and `React.Children.toArray` maps
  // `null`/`undefined` to `[]`. This wrapper is optional-children on purpose (56 call sites), so an
  // absent child is handed over as that same empty list: what MUI renders is unchanged, and the value
  // now matches the declaration instead of being asserted to.
  const children: NonNullable<React.ReactNode> = props.children ?? [];

  return (
    <Accordion
      {...accordionProps}
      className={props.className}
      ref={accordianRef}
      disableGutters={true}
      classes={{ region: "h-full" }}
      sx={
        {
          boxShadow: "none",
          border: border ? border : "",
          borderRadius: borderRadius ? `${borderRadius} !important` : "",
          "&::before": {
            height: "0", //For removing extra top line
          },
          "&.MuiPaper-root": {
            height: "auto",
          },
          "& .MuiAccordionDetails-root": {
            height: "100%",
            width: "100%",
            padding: "0.5rem",
            transition: "opacity 0.3s ease, transform 0.3s ease",
            opacity: 0,
            transform: "translateY(-0.25rem)",
          },
          "& .MuiAccordionSummary-root": {
            width: "100%",
            padding: "0",
          },
          "& .MuiAccordionSummary-content": {
            width: "100%",
            margin: 0,
          },
          "& .MuiButtonBase-root.MuiListItemButton-root.Mui-selected": {
            backgroundColor: "#CAECFF",
          },
          "&.Mui-expanded .MuiAccordionDetails-root": {
            opacity: 1,
            transform: "translateY(0)",
          },
          ...sx,
        } as SxProps<Theme>
      }
    >
      {children}
    </Accordion>
  );
});
Accordian.displayName = "Accordian";
export default Accordian;
